import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { CallLogQueryKeywords } from '../../../shared/expression-editor/call-logs-query-editor.component';
import { AuthenticationContext } from '../../../shared/contexts/authentication.context';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../button/button.component';
import {
  CallRecordsParams,
  DetailedCallRecordDirection,
  DetailedCallRecordsReportResource,
} from '../shared/detailed-call-records-report.resource';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ActivatedRoute } from '@angular/router';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { HumanReadableDateTimePipe } from '../shared/human-readable-date-time.pipe';
import { TranslateService } from '@ngx-translate/core';
import { CallRecordsDatabaseEntriesResource } from '../shared/call-records-database-entries.resource';
import { CallRecordsDatabaseEntriesInfo, CurrentUser } from '../../../shared/models/generated/ziroModels';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import {
  CallRecordEntity,
  CallRecordSearchType,
  PstnCallRecordQueryEditorFieldComponent,
} from './pstn-call-record-query-editor-field/pstn-call-record-query-editor-field.component';
import { isEqual } from 'lodash';
import { CallRecordsRowMapperService } from './call-records-row-mapper.service';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';

export interface CallRecordsRow extends DatatableRow {
  rowId: string;
  callTypeCode: string;
  duration: number;
  startDateTime: string;
  to: string;
  from: string;
  toSubtext: string;
  fromSubtext: string;
}

interface CallRecordFormData {
  callRecordQuery: CallRecordEntity;
}

@Component({
  selector: 'ziro-pstn-call-record-finder',
  templateUrl: './pstn-call-record-finder.component.html',
  styleUrls: ['../../reporting.scss', './pstn-call-record-finder.component.scss'],
  standalone: false,
})
export class PstnCallRecordFinderComponent
  extends ZiroFormAbstractDirective<CallRecordFormData>
  implements OnInit, OnDestroy
{
  private _authenticationContext = inject(AuthenticationContext);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _callRecordsDatabaseEntriesResource = inject(CallRecordsDatabaseEntriesResource);
  private _callRecordsRowMapper = inject(CallRecordsRowMapperService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _dateTimePipe = inject(HumanReadableDateTimePipe);
  private _detailedCallRecordsResource = inject(DetailedCallRecordsReportResource);
  private _route = inject(ActivatedRoute);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);

  private _subscriptions = new Subscription();
  private _exportButtonId = 'export-call-records-button';

  @ViewChild(DatatableComponent) datatable: DatatableComponent<CallRecordsRow>;

  displayEditor = false;
  query: CallRecordsParams;
  keywords = CallLogQueryKeywords;
  tenantId: string;
  showCustomAlert = false;
  tableDataAutomation = 'pstn-call-record-finder-datatable';
  datatableRows: CallRecordsRow[] = [];
  tableColumns: DatatableColumn<CallRecordsRow>[] = [
    {
      name: 'startDateTime',
      label: 'tkey;reports.microsoft.pstn_call_record_finder.table.column.start_date.label',
      sortFn: (a: string, b: string) => {
        const parseDate = (str: string): number => {
          const datePattern = /[A-Z][a-z]{2}\s\d{1,2},\s\d{4},\s\d{1,2}:\d{2}\s[AP]M/;
          const match = str.match(datePattern);
          return match ? new Date(match[0]).getTime() : 0;
        };
        return parseDate(a) - parseDate(b);
      },
    },
    {
      name: 'from',
      label: 'tkey;reports.microsoft.pstn_call_record_finder.table.column.from.label',
      colClass: 'custom-align',
    },
    {
      name: 'to',
      label: 'tkey;reports.microsoft.pstn_call_record_finder.table.column.to.label',
      colClass: 'custom-align',
    },
    {
      name: 'duration',
      label: 'tkey;reports.microsoft.pstn_call_record_finder.table.column.duration.label',
      columnCharacterWidth: 20,
    },
    {
      name: 'callTypeCode',
      label: 'tkey;reports.microsoft.pstn_call_record_finder.table.column.call_type_code.label',
      columnCharacterWidth: 25,
    },
  ];
  oldestCdrRecordDate: string;
  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    this.tenantId = this._route.snapshot.paramMap.get('tenantId');
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;reports.microsoft.pstn_call_record_finder.title' }]);

    this.formConfig = {
      fields: {
        callRecordQuery: {
          validation: [
            {
              validator: () => {
                const callRecordQueryComponent = this.fieldComponents.find(
                  (field) => field.fieldId() === 'callRecordQuery',
                ) as PstnCallRecordQueryEditorFieldComponent;
                return callRecordQueryComponent.hasValidationError
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
            },
          ],
        },
      },
    };

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((changes) => {
      const query = this.entity?.callRecordQuery;
      if (!query) {
        return;
      }
      if (!isEqual(changes.new, changes.old)) {
        this._runQueryFromNaturalLanguageEditor(query);
      }
    });
    this._subscriptions.add(formUpdateSub);

    const subs = combineLatest([
      this._authenticationContext.state$,
      this._callRecordsDatabaseEntriesResource.getInfo(this.tenantId),
    ]).subscribe(([user, info]: [CurrentUser, CallRecordsDatabaseEntriesInfo]) => {
      this.oldestCdrRecordDate = info.oldestCallRecordDate;
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 7);
      this.entitySource.next({
        callRecordQuery: {
          callDirectionType: DetailedCallRecordDirection.BOTH,
          callRecordSearchType: CallRecordSearchType.USER_PRINCIPAL_NAME,
          searchValue: user.userId,
          fromDate: from,
          toDate: to,
        },
      });
      this.displayEditor = true;
      this._initialBottomNav();
    });
    this._subscriptions.add(subs);
    const statusObs = this._callRecordsDatabaseEntriesResource
      .getStatus(this.tenantId)
      .pipe(
        map((status) => {
          this._setBottomNavTimestamp(status.lastRunTime);
        }),
      )
      .subscribe();
    this._subscriptions.add(statusObs);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  getDateFormatted(): string {
    return this._dateTimePipe.transform(this.oldestCdrRecordDate);
  }

  searchSubTextsWithinRow = (searchValue: string, row: CallRecordsRow): boolean => {
    return row.fromSubtext?.includes(searchValue) || row.toSubtext?.includes(searchValue);
  };

  protected submit(): Observable<void> {
    return of(null);
  }

  private _forceDatatableLoading(isLoading: boolean): void {
    if (this.datatable) {
      this.datatable.isViewLoading = isLoading;
    }
  }

  private _initialBottomNav(): void {
    const bottomNavButtons: BottomNavButton[] = [];

    bottomNavButtons.push({
      id: this._exportButtonId,
      dataAutomation: 'call-records-export-button',
      label: 'tkey;reports.call_logs.button',
      icon: ZiroIcons.EXPORT,
      buttonClass: ButtonStyles.PRIMARY,
      state: {
        buttonDisableState: {
          disabled: false,
          tooltipKey: '',
        },
        tooltipVisible: true,
      },
      cb: () => {
        this._onExportClicked();
      },
    });

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _onExportClicked(): void {
    this._setExportPending(true);
    const queryCopy = { ...this.query };
    if (queryCopy.number) {
      queryCopy.number = encodeURIComponent(queryCopy.number);
    }
    this._detailedCallRecordsResource.export(this.tenantId, queryCopy).subscribe(() => {
      this._setExportPending(false);
      this._toastService.push(
        ToastTypes.SUCCESS,
        ZiroIcons.EXPORT,
        'tkey;reports.xlsx_export.downloaded_toast.title',
        'tkey;reports.xlsx_export.downloaded_toast.message',
      );
    });
  }

  private _setExportDisabled(disabled: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: this._exportButtonId,
        state: {
          pending: false,
          buttonDisableState: { disabled, tooltipKey: 'tkey;reports.call_logs.button.tooltip' },
          tooltipVisible: disabled,
        },
      }),
    );
  }

  private _setExportPending(isPending: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: this._exportButtonId,
        state: {
          pending: isPending,
          buttonDisableState: { disabled: isPending, tooltipKey: '' },
          tooltipVisible: true,
        },
      }),
    );
  }

  private _setBottomNavTimestamp(timestamp: string): void {
    if (!timestamp) {
      return;
    }
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${ZiroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.microsoft.pstn_call_record_finder.last_run_time',
              )} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
            `,
      }),
    );
  }

  private _runQueryFromNaturalLanguageEditor(callRecordQuery: CallRecordEntity): void {
    const fromDate: Date | undefined = callRecordQuery?.fromDate;
    const toDate: Date | undefined = callRecordQuery?.toDate;

    if (!callRecordQuery.searchValue) {
      return;
    }
    if (!fromDate || !toDate) {
      return;
    }
    if (fromDate.getTime() > toDate.getTime()) {
      return;
    }

    const query: CallRecordsParams = {
      direction: callRecordQuery.callDirectionType,
      startDate: fromDate.toISOString(),
      endDate: toDate.toISOString(),
    };

    switch (callRecordQuery.callRecordSearchType) {
      case CallRecordSearchType.USER_PRINCIPAL_NAME:
        query.userPrincipalName = callRecordQuery.searchValue;
        break;
      case CallRecordSearchType.DISPLAY_NAME:
        query.displayName = callRecordQuery.searchValue;
        break;
      case CallRecordSearchType.NUMBER:
        query.number = callRecordQuery.searchValue;
        break;
      default:
        query.userPrincipalName = callRecordQuery.searchValue;
        break;
    }

    this.query = query;
    this._forceDatatableLoading(true);

    this._detailedCallRecordsResource
      .getCallRecords(this.tenantId, this.query)
      .pipe(
        tap((callRecords) => {
          this.showCustomAlert = false;
          this._setExportDisabled(!callRecords.length);

          this.datatableRows = this._callRecordsRowMapper.mapRows({
            records: callRecords,
            query,
            searchBy: callRecordQuery!.callRecordSearchType,
          });
        }),
        catchError((error) => {
          this.datatableRows = [];
          if (error.status === 422 && error.error.reasonCode === 'CDR_QUERY_LIMIT_EXCEEDED') {
            this.showCustomAlert = true;
            return of(null);
          }
          throw error;
        }),
        finalize(() => this._forceDatatableLoading(false)),
      )
      .subscribe();
  }
}
